import React, { useState } from 'react'
import { Button } from '@reapit/elements/core/button'
import { AddIcon } from '@reapit/elements/icons/add'
import { toast } from '@reapit/elements/core/toaster'
import { AdminPdfTemplate } from '../../../../api/types'
import { useAdminPdfTemplateFields, useAdminPdfTemplates, usePdfTemplateDownload } from '../../../../api/pdf-templates'
import { useBuilderDeletes } from '../../../../api/builder-deletes'
import { PdfTemplateDrawer } from '../pdf-template-drawer'
import { ConfirmDeleteDialog } from '../../../../components/confirm-delete-dialog'
import { useAccessMode } from '../../../../access-mode-provider'
import { EmptyState, SideCardHelp } from '../../style'
import {
  PdfActions,
  PdfBadge,
  PdfFieldRow,
  PdfFieldsPanel,
  PdfTabIntro,
  PdfTabLayout,
  PdfTemplateCard,
  PdfTemplateHeader,
  PdfTemplateTitle,
} from './style'

interface PdfTemplatesTabProps {
  formReferenceID: string
  versionReferenceID: string
}

/**
 * The PDF Templates tab: upload the form/annexure PDFs, list them, view the extracted AcroForm fields, and
 * rename/flag them. Field mapping (linking to merge/form fields) is a later milestone.
 */
export const PdfTemplatesTab: React.FC<PdfTemplatesTabProps> = ({ formReferenceID, versionReferenceID }) => {
  const { data: templates, refresh } = useAdminPdfTemplates(formReferenceID, versionReferenceID)
  const [expandedRef, setExpandedRef] = useState<string | null>(null)
  const { data: fields, loading: fieldsLoading } = useAdminPdfTemplateFields(
    formReferenceID,
    versionReferenceID,
    expandedRef ?? undefined,
  )
  const download = usePdfTemplateDownload(formReferenceID, versionReferenceID)
  const { isReadOnly } = useAccessMode()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<AdminPdfTemplate | null>(null)

  const { deletePdfTemplate } = useBuilderDeletes()
  const [removing, setRemoving] = useState<AdminPdfTemplate | null>(null)
  const [deleting, setDeleting] = useState(false)

  const confirmRemove = async () => {
    if (!removing) return
    setDeleting(true)
    try {
      await deletePdfTemplate(formReferenceID, versionReferenceID, removing.referenceID)
      setRemoving(null)
      refresh()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not remove this template.')
    } finally {
      setDeleting(false)
    }
  }

  const openUpload = () => {
    setEditing(null)
    setIsDrawerOpen(true)
  }
  const openEdit = (template: AdminPdfTemplate) => {
    setEditing(template)
    setIsDrawerOpen(true)
  }
  const handleDownload = (referenceID: string) => {
    download(referenceID).catch((error) =>
      toast.error(error instanceof Error ? error.message : 'Could not open the PDF.'),
    )
  }

  return (
    <PdfTabLayout>
      <PdfTabIntro>
        <SideCardHelp>
          Upload the PDF documents this form produces — the primary form and any annexures. View the fields extracted
          from each PDF, download the original, or edit its details.
        </SideCardHelp>
        {!isReadOnly && (
          <Button variant="primary" size="small" iconLeft={<AddIcon />} type="button" onClick={openUpload}>
            Upload PDF
          </Button>
        )}
      </PdfTabIntro>

      {templates.length === 0 ? (
        <EmptyState>No PDF templates yet. Upload the form or an annexure to get started.</EmptyState>
      ) : (
        templates.map((template) => {
          const expanded = expandedRef === template.referenceID
          return (
            <PdfTemplateCard key={template.referenceID}>
              <PdfTemplateHeader>
                <PdfTemplateTitle>{template.name || 'Untitled PDF'}</PdfTemplateTitle>
                <PdfBadge>{template.isAnnexure ? 'Annexure' : 'Form document'}</PdfBadge>
                {template.repeatable && <PdfBadge>Repeatable</PdfBadge>}
              </PdfTemplateHeader>
              <PdfActions>
                <Button
                  variant="tertiary"
                  useLinkStyle
                  size="small"
                  type="button"
                  onClick={() => setExpandedRef(expanded ? null : template.referenceID)}
                >
                  {expanded ? 'Hide fields' : 'View fields'}
                </Button>
                <Button
                  variant="tertiary"
                  useLinkStyle
                  size="small"
                  type="button"
                  onClick={() => handleDownload(template.referenceID)}
                >
                  Download
                </Button>
                {!isReadOnly && (
                  <>
                    <Button
                      variant="tertiary"
                      useLinkStyle
                      size="small"
                      type="button"
                      onClick={() => openEdit(template)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="tertiary"
                      useLinkStyle
                      isDestructive
                      size="small"
                      type="button"
                      onClick={() => setRemoving(template)}
                    >
                      Delete
                    </Button>
                  </>
                )}
              </PdfActions>
              {expanded && (
                <PdfFieldsPanel>
                  {fieldsLoading ? (
                    <PdfFieldRow>Loading extracted fields…</PdfFieldRow>
                  ) : fields.length === 0 ? (
                    <PdfFieldRow>No AcroForm fields found in this PDF.</PdfFieldRow>
                  ) : (
                    fields.map((field) => (
                      <PdfFieldRow key={field.fieldName}>
                        <span>{field.fieldName}</span>
                      </PdfFieldRow>
                    ))
                  )}
                </PdfFieldsPanel>
              )}
            </PdfTemplateCard>
          )
        })
      )}

      <PdfTemplateDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        formReferenceID={formReferenceID}
        versionReferenceID={versionReferenceID}
        template={editing}
        onSaved={() => refresh()}
      />
      <ConfirmDeleteDialog
        isOpen={Boolean(removing)}
        title="Delete PDF template"
        message={`Delete "${removing?.name || 'this template'}"? This can't be undone.`}
        busy={deleting}
        onConfirm={confirmRemove}
        onCancel={() => setRemoving(null)}
      />
    </PdfTabLayout>
  )
}
